import { TextField, InputAdornment, IconButton } from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { WidgetProps } from "@rjsf/utils";
import { useState, useRef } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { inputStyles, renderLabel, renderDescriptionTooltip } from "./common";

dayjs.extend(utc);

// DateTime Widget for combined date and time input
export const DateTimeWidget = (props: WidgetProps) => {
  const { id, value, label, onChange, required, disabled, readonly, schema } = props;
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value;
    if (val === "") {
      setError(null);
      onChange(undefined);
      return;
    }
    
    let finalValue = val;
    // If user enters only a date (no time component), default time to 00:00
    if (val.match(/^\d{2}-\d{2}-\d{4}$/) && !val.includes("T")) {
      finalValue = `${val}T00:00:00`;
    }
    // Parse as local time (datetime-local input provides local time)
    const selectedDate = dayjs(finalValue);

    // Reject datetimes in the future (compare in local time)
    if (selectedDate.isAfter(dayjs())) {
      setError("Date and time cannot be in the future");
      return;
    }
    setError(null);

    // Convert local time to UTC and format according to schema pattern
    if (schema.pattern === "^\\d{4}-\\d{2}-\\d{2} \\d{2}:\\d{2}:\\d{2}Z$") {
      // Convert to UTC, then format as "YYYY-MM-DD HH:mm:ssZ"
      finalValue = selectedDate.utc().format("YYYY-MM-DD HH:mm:ss[Z]");
    } else if (schema.pattern === "^(?:(?:\\d\\d[2468][048]|\\d\\d[13579][26]|\\d\\d0[48]|[02468][048]00|[13579][26]00)-02-29|\\d{4}-(?:(?:0[13578]|1[02])-(?:0[1-9]|[12]\\d|3[01])|(?:0[469]|11)-(?:0[1-9]|[12]\\d|30)|(?:02)-(?:0[1-9]|1\\d|2[0-8])))T(?:(?:[01]\\d|2[0-3]):[0-5]\\d(?::[0-5]\\d(?:\\.\\d+)?)?(?:Z))$") {
      // Convert to UTC ISO format
      finalValue = selectedDate.toISOString();
    } else {
      // Default: convert to UTC ISO format for any datetime value
      finalValue = selectedDate.toISOString();
    }
    
    onChange(finalValue);
  };

  const getDisplayValue = (val: string | undefined) => {
    if (!val) return "";
    // Parse the date (handles UTC strings like "2024-01-01T12:00:00Z")
    // and convert to local time for display in datetime-local input
    const date = dayjs(val);
    return date.isValid() ? date.format("YYYY-MM-DDTHH:mm") : "";
  };

  return (
    <TextField
      id={id}
      fullWidth
      type="datetime-local"
      value={getDisplayValue(value)}
      onChange={handleChange}
      disabled={disabled}
      error={!!error}
      helperText={error}
      inputRef={inputRef}
      slotProps={{
        input: {
          readOnly: readonly,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => {
                  if (inputRef.current && "showPicker" in inputRef.current) {
                    (inputRef.current as any).showPicker();
                  }
                }}
                edge="end"
                size="small"
                sx={{ mr: schema.description ? 1 : 0 }}
              >
                <CalendarTodayIcon fontSize="small" />
              </IconButton>
              {schema.description && renderDescriptionTooltip(schema.description)}
            </InputAdornment>
          ),
        },
        inputLabel: {
          shrink: true,
        },
        htmlInput: {
          // Set max to current local time in the format expected by datetime-local
          max: dayjs().format("YYYY-MM-DDTHH:mm"),
        },
      }}
      label={renderLabel(label, !!required)}
      required={required}
      size="small"
      variant="outlined"
      sx={inputStyles}
    />
  );
};
